import { FC, useEffect, useMemo } from 'react';
import cs from 'classnames';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize } from '@lemon8/design';
import { $Locale, $UserAgent } from '@lemon8/web-app-shared/atom';
import { $ShareInfo } from '~/models/share-info';
import { CAMPAIGN_ID, sslocal, snssdk2657 } from '../constants';
import { $ReferralGiftsData } from '~/models/referral-gifts';
import { createActivitiesOnelink } from '~/utils/onelink';
import { useGiftConfig } from '../hooks/use-gift-config';
import { Region } from '@lemon8/web-app-shared/i18n';

import './style.scss';

export const Body: FC = () => {
  const {
    inviteCode,
    userName,
    avatarUrl,
    activityStatus,
    showTimeRange,
    giftType = '',
    amountStr = '',
    fromUid = '',
  } = useAtomValue($ReferralGiftsData);

  const { region } = useAtomValue($Locale);
  const { isInApp } = useAtomValue($UserAgent);
  const { sharePlatform } = useAtomValue($ShareInfo);

  const { t } = useTranslation();

  const config = useGiftConfig({ giftType, region, amountStr });

  const shortUserName = useMemo(() => {
    if (userName) {
      if (userName.length >= 2) {
        return `${userName.at(0)}***${userName.at(-1)}`;
      } else {
        return `${userName}***`;
      }
    } else {
      return '***';
    }
  }, [userName]);

  const commonParams: Record<string, string> = {
    activity_name: CAMPAIGN_ID,
    gift_type: giftType,
    share_platform: sharePlatform,
    shifu_uid: fromUid,
  };

  const onClick = (): void => {
    if (isInApp) {
      // 端内进行了点击，直接跳转活动页面
      window.location.href = sslocal;
      return;
    }

    window.Tea('reflow_page_click', commonParams);

    const oneLink = createActivitiesOnelink({
      schema: snssdk2657,
      campaignId: CAMPAIGN_ID,
      extraDeepLinkValueData: {
        invite_code: inviteCode,
        raw_data: JSON.stringify({ amount: amountStr }),
      },
      ampExtra: {
        from_user_id: fromUid,
      },
    });

    oneLink.active();
  };

  useEffect(() => {
    if (isInApp) {
      // 端内打开了回流页，跳转到活动承接页
      window.location.href = sslocal;
    } else {
      window.Tea('reflow_page_show', commonParams);
    }
  }, []);

  useEffect(() => {
    const main = document.querySelectorAll('main')?.[0];
    if (main) {
      if (region === Region.jp) {
        main.style.backgroundColor = '#ff9dc3';
      } else if (region === Region.th) {
        main.style.backgroundColor = '#b6fabf';
      }
    }
  }, [region]);

  if (region !== Region.th && region !== Region.jp) {
    return null;
  }

  return (
    <div className={cs('referral-gifts', `${region}-background`)}>
      <div className="referral-gifts-avatar">{avatarUrl && <img src={avatarUrl} />}</div>
      <div className={cs('referral-gifts-info', `${region}-info`)}>
        {t('couponShare_reflow_subtitle').replace('{username}', shortUserName)}
      </div>
      <div className={cs('referral-gifts-title', `${region}-title`)} />
      <div className={cs('referral-gifts-time', `${region}-time`)}>
        {showTimeRange !== '' && <div>{showTimeRange}</div>}
        {(activityStatus === 3 || activityStatus === 4) && <div>{t('activity_ended')}</div>}
      </div>
      <div className={cs('referral-gifts-image', `gift-type-${giftType}`)} />
      <div className="referral-gifts-rules">
        <div
          className={cs('referral-gifts-rules-title', `gift-type-color-${giftType}`)}
          dangerouslySetInnerHTML={{ __html: config.ruleTitle }}
        />
        <ul className="referral-gifts-rules-text">
          <li>{t('couponShare_reflow_howTo_body_1')}</li>
          <li>
            {t('couponShare_reflow_howTo_body_2').replace('{search_word}', t('couponShare_reflow_howTo_searchWord'))}
          </li>
          <li
            dangerouslySetInnerHTML={{
              __html: t('couponShare_reflow_howTo_body_3').replace('{code}', `<span>${inviteCode}</span>`),
            }}
          ></li>
          <li>{config.ruleAmount}</li>
        </ul>
      </div>
      <div className="referral-gifts-bottom">
        <Button
          className="referral-gifts-bottom-button"
          title={t('couponShare_reflow_CTA_open')}
          variant="Primary"
          size={ButtonSize.large}
          onClick={onClick}
        />
      </div>
    </div>
  );
};
